import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
/* WP-37 — the one caption primitive, from the shared engine. */
import { TableCaption } from '../../../table/list-table';
import { ListSkeleton } from '../dashboard-shared-components/load-state';
import toast from '../../../../utils/toast';
import { deleteUnit, listUnits } from '../../../../api/hierarchy-client';
import { UnitListItem } from '../../../../interfaces/hierarchy-interfaces';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useUnitAllowance } from '../../../hooks/use-unit-allowance';
import { ActionWithReason } from '../dashboard-shared-components/disabled-reason';
import { AllocateUnitsPanel } from '../../../units/allocate-units-panel';
import {
  Presence,
  Staleable,
  StateSwitch,
  StatusLabel,
  listItemMotion,
  scrimMotion,
  useOverlayMotion,
} from '../../../../motion';
import {
  Banner,
  DangerButton,
  Empty,
  EmptyText,
  EmptyTitle,
  Field,
  FilterToggle,
  GhostButton,
  Header,
  HeaderButtons,
  HeaderText,
  Label,
  Modal,
  ModalActions,
  ModalHeader,
  ModalSub,
  ModalTitle,
  Muted,
  PrimaryButton,
  ResultCount,
  RowActions,
  RowButton,
  RowDangerButton,
  Scrim,
  Select,
  Sub,
  Table,
  TableWrap,
  Tbody,
  Td,
  TdRight,
  Th,
  ThRight,
  Thead,
  Title,
  Toolbar,
  ToolbarField,
  ToolbarSpacer,
  Tr,
  UnitName,
  UnitNameEmpty,
  UnitNumber,
  UnitSearchInput,
  Wrap,
} from './dashboard-units.styled';
import { DashboardUnitForm } from './dashboard-unit-form';
import { UnitPromoteModal, useBuildingStaysUnits, useUnitStays } from './unit-stays-card';
import { staysIsOn, useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import {
  AreaUnitToggle,
  KindPill,
  OccupancyPill,
  formatArea,
  formatBathrooms,
  groupByPortfolio,
  readError,
  useBuildings,
  useMeasurementSystem,
  useRememberedBuilding,
} from './units-shared';
import { useDialogFocus } from '../../../hooks/use-dialog-focus';
import { ModalPortal } from '../../../modal/modal-portal';

/**
 * UnitsForBuilding — pick a building, work through its units.
 *
 * Units only exist inside a building, so a building must be chosen before there
 * is anything to show. The choice is remembered across visits (see
 * useRememberedBuilding) because staff live in one building at a time.
 */

// ONE VERB (R-20). This screen used to offer Archive and "Delete permanently"
// side by side and make the user work out which one the database would allow —
// and then, when it guessed wrong, offer an "Archive instead" escape hatch that
// on an occupied unit looped on the identical 409 (WP-30).
//
// There is one control now. The server decides whether the row goes or is
// retained as archived, and either way the answer is "deleted". The only
// refusal left is somebody still living there, which is a fact about the world
// rather than a choice between two words, so it is shown and the dialog stays
// open.
interface DeleteState {
  unit: UnitListItem;
  conflict: string;
  busy: boolean;
}

// WP-29. The server refuses a delete while an occupancy is still open, and the
// count that decides it is ALREADY on the row — `is_vacant` is what the
// OccupancyPill two cells to the left renders. There was no reason for the user
// to find this out by clicking and reading a 409.
//
// Returning the sentence rather than a boolean keeps the disabled state and the
// explanation from drifting apart, which is the shape dashboard-portfolios.tsx
// already uses for the default portfolio and states as the intended standard.
const deleteBlockedReason = (unit: UnitListItem): string | null =>
  unit.is_vacant
    ? null
    : `Unit ${unit.unit_number} still has someone living in it. Move them out on the roster first.`;

export const DashboardUnits: React.FC = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const {
    buildings,
    loading: buildingsLoading,
    error: buildingsError,
  } = useBuildings();

  // null when grouping would add nothing (one portfolio, or the caller cannot
  // read portfolios at all) — the picker then renders its flat list.
  const buildingGroups = useMemo(
    () => groupByPortfolio(buildings),
    [buildings]
  );
  const [buildingId, setBuildingId] = useRememberedBuilding();

  const [units, setUnits] = useState<UnitListItem[]>([]);
  /* SEARCH. This table rendered `units.map(...)` unconditionally — no search,
     no pagination, no sort — and a building is a list of units by definition:
     a tower with 300 doors put 300 rows on one screen. The question asked here
     is almost always about ONE door. */
  const [unitQuery, setUnitQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vacantOnly, setVacantOnly] = useState(false);
  /* THE "INCLUDE ARCHIVED" TOGGLE IS GONE, and deliberately, though an earlier
     WP added it for a good reason at the time: archiving was reachable from the
     edit form and nothing could show an archived unit again.
     Under R-20 archived IS how a deleted unit with occupancy history is stored,
     so a toggle that reveals those rows is a view of deleted records — which
     R-15 rules out. Nothing in the product archives a unit any more, and
     `listUnits` still accepts `includeArchived` for ops use. */
  const [system, setSystem] = useMeasurementSystem();
  // The org's prepaid pool. Read once here and handed down, so the allocation
  // panel and this screen can never show two different "units available".
  const allowance = useUnitAllowance();

  const [formUnit, setFormUnit] = useState<UnitListItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DeleteState | null>(null);
  const loadRef = useRef(0);
  const deleteRef = useRef(false);
  const deleteDialogRef = useDialogFocus<HTMLDivElement>(
    Boolean(pendingDelete),
    () => {
      if (!pendingDelete?.busy) {
        setPendingDelete(null);
      }
    }
  );

  const deleteModalMotion = useOverlayMotion('modal');

  const canCreate = can(permissions, PERMISSIONS.UNIT_CREATE);

  // WP-43. ONE string, and it is both the disabled test and the visible reason.
  // Keeping them as two expressions is how a control ends up disabled for a
  // reason nobody is told — which is what this screen did.
  //
  // Order matters: "pick a building first" is the actionable one, and an org
  // that is also out of units still has to pick a building before the pool is
  // even relevant. `remaining` is Infinity for an unmetered org. While the
  // allowance is still loading nothing is claimed — refusing on an unknown is
  // its own wrong answer.
  const addUnitBlockedReason = !buildingId
    ? 'Choose a building first — units belong to one.'
    : !allowance.isLoading && allowance.remaining < 1
      ? 'No prepaid units left. Buy more from Billing to add another.'
      : null;
  const canEdit = can(permissions, PERMISSIONS.UNIT_EDIT);
  const canDelete = can(permissions, PERMISSIONS.UNIT_DELETE);
  // Lobbero Stays (plan S1, S-4): a third row control — "Promote" (or why
  // not), or "On Stays" for a listed unit. Only when the organisation has Stays.
  const canPromote = can(permissions, PERMISSIONS.STAYS_PROMOTE);
  const staysOn = staysIsOn(
    useStaysEntitlement({ enabled: can(permissions, PERMISSIONS.STAYS_READ) }).state
  );
  const staysUnits = useBuildingStaysUnits(buildingId, staysOn);
  const [promoteUnitId, setPromoteUnitId] = useState<number | null>(null);
  const promoteState = useUnitStays(promoteUnitId ?? undefined, staysOn && promoteUnitId !== null);
  const showActions = canEdit || canDelete || staysOn;

  // A building the user can no longer reach (scope revoked, building archived)
  // must not sit in the picker as a selected-but-absent id.
  useEffect(() => {
    if (buildingsLoading || buildings.length === 0) {
      return;
    }

    const stillVisible = buildings.some(b => b.id === buildingId);

    if (!stillVisible) {
      setBuildingId(buildings[0].id);
    }
  }, [buildings, buildingsLoading, buildingId, setBuildingId]);

  const load = useCallback(() => {
    const request = ++loadRef.current;
    if (!buildingId) {
      setUnits([]);

      return;
    }

    setLoading(true);
    setError('');
    listUnits(buildingId, {
      ...(vacantOnly ? { vacant: true } : {}),
    })
      .then(result => {
        if (request === loadRef.current) {
          setUnits(result);
        }
      })
      .catch(err => {
        if (request === loadRef.current) {
          setUnits([]);
          setError(readError(err, 'Could not load units for this building.'));
        }
      })
      .finally(() => {
        if (request === loadRef.current) {
          setLoading(false);
        }
      });
  }, [buildingId, vacantOnly]);

  useEffect(() => {
    load();


  return () => {
      loadRef.current += 1;
    };
  }, [load]);

  const openCreate = () => {
    setFormUnit(null);
    setFormOpen(true);
  };

  const openEdit = (unit: UnitListItem) => {
    setFormUnit(unit);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormUnit(null);
  };

  const onSaved = () => {
    closeForm();
    load();
  };

  const removeUnit = async (state: DeleteState) => {
    if (deleteRef.current) {
      return;
    }

    deleteRef.current = true;
    setPendingDelete({ ...state, busy: true });
    try {
      await deleteUnit(state.unit.id);
      toast.success(`Unit ${state.unit.unit_number} deleted`);
      setPendingDelete(null);
      load();
    } catch (err) {
      // Keep the dialog open and swap in the server's explanation. There is no
      // alternative action to offer any more — the only refusal is an occupied
      // unit, and the remedy for that lives on the roster, not in here.
      setPendingDelete({
        ...state,
        busy: false,
        conflict: readError(err, 'Could not delete this unit.'),
      });
    } finally {
      deleteRef.current = false;
    }
  };

  const selectedBuilding = buildings.find(b => b.id === buildingId) ?? null;
  const unassignedCount = units.filter(u => u.kind === 'unassigned').length;

  /* Client-side: the building's units are already loaded in full, so a request
     per keystroke would add latency without adding an answer. */
  const visibleUnits = useMemo(() => {
    const needle = unitQuery.trim().toLowerCase();

    if (!needle) return units;

    return units.filter(unit =>
      [unit.unit_number, unit.name, unit.kind, unit.status, unit.floor]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [units, unitQuery]);

  /* "No name" only says something when something else on this list HAS a
     name. See the sub-label itself for the full reasoning (MR-30). */
  const someUnitIsNamed = visibleUnits.some(u => !!u.name);

  /* WHAT FILLS THE LIST BOX. The skeleton is for a load with NOTHING to show
     yet; switching buildings (or the vacant filter) keeps the rows that are
     already there and dims them under Staleable until the new list lands —
     the table no longer flashes to placeholders and back on every change. */
  const listState =
    loading && units.length === 0
      ? 'loading'
      : !buildingId
      ? 'pick'
      : units.length === 0
      ? 'empty'
      : 'ready';

  return (
    <Wrap>
      <Header>
        <HeaderText>
          <Title>Units</Title>
          <Sub>
            Every door in a building, and whether anyone is behind it right now.
          </Sub>
        </HeaderText>
        <HeaderButtons>
          {/* WP-32. The words on the button match the words on the screen it
              opens, and the words on the main-menu tile that opens the same
              screen. "View roster" here and "Who's Home" there described one
              component under two names, which is how a product ends up building
              a second one. There is ONE Who's Home destination: this component,
              hosted at /dashboard/roster for owners and managers and at /roster
              from the main menu. */}
          <GhostButton
            type="button"
            onClick={() => navigate('/dashboard/roster')}
          >
            Who&apos;s Home
          </GhostButton>
          {canCreate && (
            /* WP-43. The reason was a `title` and nothing else — a native
               tooltip, which never fires on touch (a desk tablet is this
               product's primary device), is suppressed on disabled controls in
               several browsers, and is unreliable for screen readers. It is now
               rendered as visible text and wired to the button with
               aria-describedby; the title stays for a mouse hover. */
            <ActionWithReason reason={addUnitBlockedReason}>
              {describedBy => (
                <PrimaryButton
                  type="button"
                  onClick={openCreate}
                  /* ALSO WHEN THE POOL IS EMPTY. Creating a unit spends one from
                     the prepaid allowance, and the server answers 403
                     UNIT_LIMIT when there is none left — so this button used to
                     open a form, take every field, and fail on submit. The
                     allowance is already loaded on this screen for the panel
                     below; disabling on it turns a wasted form into a visible
                     reason.
                     `remaining` is Infinity for an unmetered org, so this is
                     false for them. It is not disabled while the allowance is
                     still loading: refusing on an unknown is its own wrong
                     answer. */
                  disabled={Boolean(addUnitBlockedReason)}
                  aria-describedby={describedBy}
                  title={addUnitBlockedReason || undefined}
                >
                  + Add unit
                </PrimaryButton>
              )}
            </ActionWithReason>
          )}
        </HeaderButtons>
      </Header>

      <Toolbar>
        <ToolbarField>
          <Label htmlFor="unit-search">Search</Label>
          <UnitSearchInput
            id="unit-search"
            type="search"
            value={unitQuery}
            placeholder="Unit number, label, kind or status"
            onChange={event => setUnitQuery(event.target.value)}
          />
        </ToolbarField>

        <ToolbarField>
          <Label htmlFor="building">Building</Label>
          <Select
            id="building"
            value={buildingId ?? ''}
            disabled={buildingsLoading || buildings.length === 0}
            onChange={event =>
              setBuildingId(
                event.target.value ? Number(event.target.value) : null
              )
            }
          >
            {buildingsLoading && <option value="">Loading buildings…</option>}
            {!buildingsLoading && buildings.length === 0 && (
              <option value="">No buildings available</option>
            )}
            {/* Grouped by portfolio once there is more than one, so an owner
                with buildings across several portfolios can find the right
                one. Falls back to the flat list otherwise. */}
            {buildingGroups
              ? buildingGroups.map(group => (
                  <optgroup key={group.portfolio} label={group.portfolio}>
                    {group.buildings.map(building => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </optgroup>
                ))
              : buildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
          </Select>
        </ToolbarField>

        <Field>
          <FilterToggle
            type="button"
            active={vacantOnly}
            aria-pressed={vacantOnly}
            onClick={() => setVacantOnly(current => !current)}
          >
            {vacantOnly ? '✓ Vacant only' : 'Vacant only'}
          </FilterToggle>
        </Field>

        {/* Display only — sizes are stored in square feet either way, so this
            is safe to flip at any time and never touches a row. */}
        <Field>
          <AreaUnitToggle system={system} onChange={setSystem} />
        </Field>

        <ToolbarSpacer />

        {!loading && units.length > 0 && (
          <ResultCount>
            {units.length} {units.length === 1 ? 'unit' : 'units'}
            {vacantOnly ? ' vacant' : ''}
          </ResultCount>
        )}
      </Toolbar>

      {buildingsError && <Banner tone="alert">{buildingsError}</Banner>}
      {error && <Banner tone="alert">{error}</Banner>}

      {/* The middle step of the flow — a building exists, now put units in it.
          Shown to anyone who can create units, because running out is something
          they need explained whether or not they are the one who can pay.

          KEYED ON THE BUILDING. The panel holds the typed quantity in local
          state and clears it only after a successful allocate, while submit()
          reads the CURRENT buildingId — so without this key a "40" typed for one
          building was still in the box after switching the picker, and one click
          put those 40 units on the other building. Remounting is the whole fix:
          a different building starts from an empty field.

          Nothing is lost by remounting. The only other state in there is the
          purchase dialog, which is portaled to <body> as a focus-trapped
          alertdialog over a full-viewport scrim — the picker behind it takes
          neither a click nor a Tab while it is open, so a building switch cannot
          happen mid-purchase. */}
      {canCreate && buildingId && selectedBuilding && (
        <AllocateUnitsPanel
          key={buildingId}
          buildingId={buildingId}
          buildingName={selectedBuilding.name}
          allowance={allowance}
          onAllocated={load}
        />
      )}

      {/* Migration placeholders are surfaced, never hidden: each one is a
          resident the front desk cannot yet look up by door. */}
      {!loading && unassignedCount > 0 && !vacantOnly && (
        <Banner tone="warning">
          {unassignedCount === 1
            ? '1 unit still needs a real unit number assigned.'
            : `${unassignedCount} units still need a real unit number assigned.`}
        </Banner>
      )}

      <StateSwitch state={listState} minHeight={200}>
      {listState === 'loading' ? (
        <ListSkeleton rows={6} label="units" />
      ) : listState === 'pick' ? (
        <Empty>
          <EmptyTitle>Pick a building</EmptyTitle>
          <EmptyText>Choose a building above to see its units.</EmptyText>
        </Empty>
      ) : listState === 'empty' ? (
        <Empty>
          <EmptyTitle>
            {vacantOnly ? 'No vacant units' : 'No units yet'}
          </EmptyTitle>
          <EmptyText>
            {vacantOnly
              ? 'Every unit in this building currently has someone in it.'
              : canCreate
              ? `Add the first unit in ${
                  selectedBuilding?.name ?? 'this building'
                } to start tracking who lives where.`
              : 'No units have been added to this building yet.'}
          </EmptyText>
          {vacantOnly && (
            <GhostButton type="button" onClick={() => setVacantOnly(false)}>
              Show all units
            </GhostButton>
          )}
        </Empty>
      ) : (
        <Staleable stale={loading}>
        <TableWrap>
          <Table>
            {/* The table's accessible name (WP-37). Visually hidden — the
                heading above already says this to a sighted reader — but it is
                what names the table when it is reached out of context. */}
            <TableCaption>
              Units in this building, with kind, floor, size and who is in them
            </TableCaption>
            <Thead>
              <tr>
                <Th>Unit</Th>
                <Th>Kind</Th>
                <Th>Floor</Th>
                <Th>Beds / baths</Th>
                <Th>Size</Th>
                <Th>Occupancy</Th>
                {showActions && <ThRight>Actions</ThRight>}
              </tr>
            </Thead>
            <Tbody>
              {visibleUnits.map((unit, index) => (
                /* listItemMotion = the capped cascade; layout={false} because
                   this list is unbounded (300 doors) and no Presence wraps it:
                   rows arrive, they do not animate out or reflow. */
                <Tr
                  key={unit.id}
                  {...listItemMotion(index)}
                  layout={false}
                  clickable
                  role="link"
                  tabIndex={0}
                  aria-label={`Open unit ${unit.unit_number}`}
                  onClick={() => navigate(`/dashboard/units/${unit.id}`)}
                  onKeyDown={event => {
                    if (event.target !== event.currentTarget) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/dashboard/units/${unit.id}`);
                    }
                  }}
                >
                  <Td data-label="Unit">
                    <UnitNumber>{unit.unit_number}</UnitNumber>
                    {/* Not an em dash: this table already spends — on "not
                        recorded" for Floor, beds/baths and Size. */}
                    {/* MR-30, THE HALF THAT WAS OPEN. units.name is nullable and
                        migration 033 documents NULL as the normal state ("NULL
                        means just the number"), so an unnamed unit is not a
                        defect — but the BULK paths never write one: the allocate
                        endpoint and signup provisioning both insert without it.
                        A customer who provisions in bulk got a wall of "No name"
                        down the column, saying nothing on any row.

                        The sub-label only means something when it
                        DISTINGUISHES — when some units carry a name and this one
                        does not. When none of them do it is noise, so it is not
                        drawn. Open question 3's own recommendation.

                        A JSX comment cannot share a ternary branch with the
                        element it describes, which is why it sits here. */}
                    {unit.name ? (
                      <UnitName>{unit.name}</UnitName>
                    ) : someUnitIsNamed ? (
                      <UnitNameEmpty>No name</UnitNameEmpty>
                    ) : null}
                  </Td>
                  <Td data-label="Kind">
                    <KindPill kind={unit.kind} />
                  </Td>
                  <Td data-label="Floor">{unit.floor || <Muted>—</Muted>}</Td>
                  <Td data-label="Beds / baths">
                    {unit.bedrooms === null && unit.bathrooms === null ? (
                      <Muted>—</Muted>
                    ) : (
                      `${unit.bedrooms ?? '—'} / ${formatBathrooms(
                        unit.bathrooms
                      )}`
                    )}
                  </Td>
                  <Td data-label="Size">
                    {unit.sq_ft === null ? (
                      <Muted>—</Muted>
                    ) : (
                      formatArea(unit.sq_ft, system)
                    )}
                  </Td>
                  <Td data-label="Occupancy">
                    <OccupancyPill state={unit} />
                  </Td>
                  {showActions && (
                    <TdRight data-label="Actions">
                      {/* The row itself navigates, so every control inside it
                          must stop the click from reaching the row. */}
                      <RowActions onClick={event => event.stopPropagation()}>
                        {canEdit && (
                          <RowButton
                            type="button"
                            /* WP-36's defect, on a screen WP-36 never reached:
                               scripts/analytics/responsive.js reported "edit"
                               x3 as one shared generic name at 1280. A reader
                               tabbing this table heard "Edit, Edit, Edit" with
                               nothing to tell them apart. The visible text
                               stays short; the NAME carries the row. */
                            aria-label={`Edit unit ${unit.unit_number}`}
                            onClick={() => openEdit(unit)}
                          >
                            Edit
                          </RowButton>
                        )}
                        {staysOn && (() => {
                          const s = staysUnits.byUnit.get(unit.id);
                          if (!s) return null;
                          if (s.listing_id) {
                            return (
                              <RowButton
                                type="button"
                                aria-label={`Open the Stays listing for unit ${unit.unit_number}`}
                                onClick={() => navigate(`/dashboard/stays/${s.listing_id}`)}
                              >
                                On Stays
                              </RowButton>
                            );
                          }
                          if (!canPromote) return null;

                          /* The reason rides on the accessible NAME and the
                             title, exactly as Delete's does beside it: a
                             paragraph under every row would push the row
                             height out at 320px. */
                          return (
                            <RowButton
                              type="button"
                              disabled={!s.eligible}
                              title={s.eligible ? undefined : s.reason ?? undefined}
                              aria-label={
                                s.eligible
                                  ? `Promote unit ${unit.unit_number} to Stays`
                                  : s.reason ?? `Unit ${unit.unit_number} cannot be promoted to Stays`
                              }
                              onClick={() => setPromoteUnitId(unit.id)}
                            >
                              Promote
                            </RowButton>
                          );
                        })()}
                        {canDelete && (
                          <RowDangerButton
                            type="button"
                            /* The reason rides on the accessible NAME rather
                               than a sibling paragraph: this is a table row, and
                               a 32ch explanation under every occupied unit's
                               button would push the row height out and force a
                               horizontal scrollbar at 320px. The fact behind it
                               is already visible in the row itself — the
                               OccupancyPill says "Occupied · 1 occupant" — so
                               nothing is hidden from a touch user, which is the
                               gap WP-43 was guarding against. */
                            disabled={deleteBlockedReason(unit) !== null}
                            title={deleteBlockedReason(unit) ?? undefined}
                            aria-label={
                              deleteBlockedReason(unit) ??
                              `Delete unit ${unit.unit_number}`
                            }
                            onClick={() =>
                              setPendingDelete({
                                unit,
                                conflict: '',
                                busy: false,
                              })
                            }
                          >
                            Delete
                          </RowDangerButton>
                        )}
                      </RowActions>
                    </TdRight>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrap>
        </Staleable>
      )}
      </StateSwitch>

      {/* Lobbero Stays: the promote form for the row's unit, opened once its
          Stays state has been read. */}
      {staysOn ? (
        <UnitPromoteModal
          open={promoteUnitId !== null && !!promoteState.state && promoteState.state.unit_id === promoteUnitId}
          state={promoteState.state}
          onClose={() => setPromoteUnitId(null)}
          onSaved={() => {
            setPromoteUnitId(null);
            staysUnits.reload();
          }}
        />
      ) : null}

      {/* Inside a Presence with a key so the form's scrim and card animate
          OUT on close (the form's own motion props do the rest). */}
      <Presence>
        {formOpen && buildingId ? (
          <DashboardUnitForm
            key="unit-form"
            buildingId={buildingId}
            unit={formUnit}
            onClose={closeForm}
            onSaved={onSaved}
          />
        ) : null}
      </Presence>

      {/* BESPOKE RATHER THAN ConfirmDialog, deliberately: the shared dialog has
          no slot for the occupied-unit refusal that keeps this one open with
          the server's sentence in it. Same motion as every other modal. */}
      <Presence>
      {pendingDelete ? (
        <ModalPortal key="unit-delete">
          <Scrim
            {...scrimMotion}
            onMouseDown={event => {
              if (event.target === event.currentTarget && !pendingDelete.busy) {
                setPendingDelete(null);
              }
            }}
          >
            <Modal
              {...deleteModalMotion}
              ref={deleteDialogRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label="Delete unit"
              onMouseDown={event => event.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  Delete unit {pendingDelete.unit.unit_number}?
                </ModalTitle>
                {/* R-20b. Losing the word "Archive" must not lose the FACT it
                    carried: the number comes back into use and the occupancy
                    history survives. Both sentences are load-bearing — the
                    first is why an admin would do this at all, the second is
                    the honesty R-14 requires alongside the word "Delete". */}
                <ModalSub>
                  Deleting frees the unit number for reuse and keeps the
                  occupancy history. The unit disappears from your lists,
                  reports and pickers, and its records stay in the audit log.
                  This cannot be undone from inside the app.
                </ModalSub>
              </ModalHeader>

              {/* No action in here any more. The one refusal left is an
                  occupied unit, and the remedy for that is a move-out on the
                  roster — not another button on this dialog. WP-29 pre-disables
                  the control so this banner becomes the rare case rather than
                  the way the rule is discovered. */}
              {pendingDelete.conflict && (
                <Banner tone="warning">
                  <div>{pendingDelete.conflict}</div>
                </Banner>
              )}

              <ModalActions>
                <GhostButton
                  type="button"
                  disabled={pendingDelete.busy}
                  onClick={() => setPendingDelete(null)}
                >
                  Cancel
                </GhostButton>
                {/* DESTRUCTIVE, AND DRESSED AS IT — kept from the two-button
                    version. This was a <PrimaryButton> once: brand teal, the
                    screen's affirmative fill, the same treatment as "Add unit"
                    and "Save", so the destructive option was the one the eye
                    lands on and a hurried reader presses by pattern. */}
                <DangerButton
                  type="button"
                  disabled={pendingDelete.busy}
                  aria-busy={pendingDelete.busy}
                  onClick={() => removeUnit(pendingDelete)}
                >
                  <StatusLabel
                    state={pendingDelete.busy ? 'busy' : 'idle'}
                    labels={{ idle: 'Delete', busy: 'Deleting…' }}
                  />
                </DangerButton>
              </ModalActions>
            </Modal>
          </Scrim>
        </ModalPortal>
      ) : null}
      </Presence>
    </Wrap>
  );
};
